import React, { useState, useEffect } from 'react';
import { MapPin, Briefcase, Clock, Users, Award, Loader, X } from 'lucide-react';
import api from '../services/api';

const ApplicationModal = ({ job, onClose }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', resume: null, coverLetter: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      data.append('coverLetter', formData.coverLetter);
      data.append('position', job.title);
      if (formData.resume) data.append('resume', formData.resume);

      await api.post('/applications', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">✓</span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
          <p className="text-gray-600 mb-6">We'll review your application and get back to you soon.</p>
          <button onClick={onClose} className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-6 py-3 rounded-lg font-semibold">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-6 border-b flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Apply for {job.title}</h3>
            <p className="text-sm text-gray-500">{job.location} • {job.employmentType?.replace('_', ' ')}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
              <input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Resume *</label>
            <input type="file" required accept=".pdf,.doc,.docx" onChange={(e) => setFormData({...formData, resume: e.target.files[0]})} className="w-full px-4 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter</label>
            <textarea rows={4} value={formData.coverLetter} onChange={(e) => setFormData({...formData, coverLetter: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500" placeholder="Tell us why you're interested..."></textarea>
          </div>
          <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <><Loader className="animate-spin" size={20} /> Submitting...</> : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
};

const Careers = () => {
  const [openings, setOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showGeneralForm, setShowGeneralForm] = useState(false);

  const companyInfo = {
    name: "Algoryn",
    benefits: ["Hands-on learning with real projects", "Mentorship and skill-building sessions", "Potential for pre-placement offer (PPO)", "Opportunity to work closely with the founder", "Letter of recommendation and internship certificate", "Flexible work hours and remote-friendly culture"],
    culture: "We foster innovation, creativity, and continuous learning in a collaborative environment."
  };

  useEffect(() => {
    const fetchOpenings = async () => {
      try {
        const res = await api.get('/careers?status=open&limit=50');
        setOpenings(res.data.careers || []);
      } catch (err) {
        console.error('Error fetching careers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOpenings();
  }, []);

  const filteredOpenings = openings.filter(job => {
    return selectedType === 'all' || job.employmentType === selectedType;
  });

  const jobTypes = [...new Set(openings.map(job => job.employmentType))];

  const formatType = (type) => type?.replace('_', ' ') || 'Full-time';

  if (loading) {
    return (
      <section id="careers" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-12 bg-gray-200 rounded mb-4 w-64 mx-auto"></div>
              <div className="h-6 bg-gray-200 rounded w-96 mx-auto"></div>
            </div>
            <div className="mt-16 grid md:grid-cols-2 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-xl h-64"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="careers" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Join Our Team</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Be part of our innovative team and help shape the future of technology
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
            <div className="flex items-center">
              <Users className="w-4 h-4 mr-1" />
              <span>50+ Team Members</span>
            </div>
            <div className="flex items-center">
              <Award className="w-4 h-4 mr-1" />
              <span>Award-winning Culture</span>
            </div>
            <div className="flex items-center">
              <Briefcase className="w-4 h-4 mr-1" />
              <span>{openings.length} Open Positions</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-12 flex flex-wrap gap-4 justify-center">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700">Type:</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              {jobTypes.map(type => (
                <option key={type} value={type}>{formatType(type)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Job Listings */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {filteredOpenings.map((job) => (
            <div key={job._id} className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:translate-y-[-2px]">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">{job.title}</h3>
                  <p className="text-sm text-gray-500">{job.department || job.employmentType}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    job.remote === 'remote' ? 'bg-green-100 text-green-800' :
                    job.remote === 'hybrid' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {job.remote || 'On-site'}
                  </span>
                </div>
              </div>

              <p className="text-gray-600 mb-4">{job.description}</p>

              <div className="space-y-2 mb-4">
                {job.location && (
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPin className="w-4 h-4 mr-1" />
                    {job.location}
                  </div>
                )}
                <div className="flex items-center text-sm text-gray-500">
                  <Clock className="w-4 h-4 mr-1" />
                  {job.experience || '0-2'} years experience
                </div>
                {job.salary && (
                  <div className="flex items-center text-sm text-gray-500">
                    <Briefcase className="w-4 h-4 mr-1" />
                    {job.salary}
                  </div>
                )}
              </div>

              {job.skills?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {job.skills.map((skill, index) => (
                    <span key={index} className="bg-white text-slate-700 px-3 py-1 rounded-full text-sm border">
                      {skill}
                    </span>
                  ))}
                </div>
              )}

              <button
                onClick={() => setSelectedJob(job)}
                className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 hover:from-cyan-500 hover:to-blue-600"
              >
                Apply Now
              </button>
            </div>
          ))}
        </div>

        {/* No results message */}
        {filteredOpenings.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">No positions found</h3>
            <p className="text-gray-600">Check back later for new opportunities.</p>
          </div>
        )}

        {/* Company Benefits */}
        <div className="bg-slate-50 rounded-xl p-8 mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Why Join {companyInfo.name}?</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {companyInfo.benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full"></div>
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
          <p className="text-gray-600 text-center italic">{companyInfo.culture}</p>
        </div>

        {/* General Application */}
        <div className="text-center">
          <p className="text-gray-600 mb-4">Don't see a position that fits? We're always looking for talented individuals.</p>
          <button onClick={() => setShowGeneralForm(true)} className="border-2 border-cyan-400 text-cyan-400 px-8 py-3 rounded-lg font-semibold hover:bg-cyan-400 hover:text-white transition-all duration-300">
            Send Us Your Resume
          </button>
        </div>

        {selectedJob && <ApplicationModal job={selectedJob} onClose={() => setSelectedJob(null)} />}
        {showGeneralForm && (
          <ApplicationModal
            job={{ title: 'General Application', location: 'Any', employmentType: 'internship' }}
            onClose={() => setShowGeneralForm(false)}
          />
        )}
      </div>
    </section>
  );
};

export default Careers;